/**
 * impersonation-session.ts — per-org customer-JWT store.
 *
 * Model: one sessionStorage slot per org (`imp_session_{orgId}`). When the
 * employee enters an org detail page the FE silently mints + caches the
 * customer token keyed by orgId. impersonation-client reads the "active org"
 * token via getImpersonationSessionForOrg(orgId).
 *
 * No global "active session" concept — each org has its own cached token.
 * The 'impersonation-change' CustomEvent still fires on mint/clear so
 * consumers can react (e.g. the org detail page chips).
 */

const keyFor = (orgId: string) => `imp_session_${orgId}`;

export interface ImpersonationSession {
  /** The customer's access token (sent as Authorization Bearer to /api/v3). */
  customerToken: string;
  /** Encrypted identity token — verified server-side by @mgmt /impersonation/verify. */
  impersonationToken: string;
  /** org_id of the customer (as string, matches organization_id). */
  orgId: string;
  /** Display name (customer company/name). */
  orgName: string;
  /** DC employee impersonating — from DC response (UX only, not authoritative). */
  impersonatorName: string;
  /** DC employee email — from DC response (UX only). */
  impersonatorEmail: string;
  /** Token expiry as absolute UNIX seconds (from JWT exp claim). */
  expSeconds: number;
  /** contact_id that was impersonated. */
  contactId: number;
}

function readRawForOrg(orgId: string): ImpersonationSession | null {
  try {
    const raw = sessionStorage.getItem(keyFor(orgId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonationSession;
    if (!parsed.customerToken || !parsed.expSeconds) return null;
    // Expire once the token's own exp passes.
    if (Date.now() / 1000 >= parsed.expSeconds) {
      sessionStorage.removeItem(keyFor(orgId));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Store an impersonation session for a specific org. Fires 'impersonation-change'. */
export function startImpersonationSession(session: ImpersonationSession): ImpersonationSession {
  sessionStorage.setItem(keyFor(session.orgId), JSON.stringify(session));
  window.dispatchEvent(new CustomEvent('impersonation-change', { detail: session }));
  return session;
}

/** Clear the impersonation session for a specific org. Fires 'impersonation-change' with null. */
export function clearImpersonationSession(orgId: string): void {
  sessionStorage.removeItem(keyFor(orgId));
  window.dispatchEvent(
    new CustomEvent<ImpersonationSession | null>('impersonation-change', { detail: null }),
  );
}

/** Return the active impersonation session for an org, or null if none / expired. */
export function getImpersonationSessionForOrg(orgId: string): ImpersonationSession | null {
  return readRawForOrg(orgId);
}

/**
 * Legacy compat — returns the session for the given orgId, or null.
 * Kept so existing imports of getImpersonationSession() still compile.
 * Callers that need org-scoped lookup should use getImpersonationSessionForOrg.
 *
 * @deprecated Pass orgId explicitly via getImpersonationSessionForOrg.
 */
export function getImpersonationSession(orgId?: string): ImpersonationSession | null {
  if (orgId) return readRawForOrg(orgId);
  // Scan all sessionStorage keys for any unexpired imp_session.
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (!k?.startsWith('imp_session_')) continue;
    const id = k.slice('imp_session_'.length);
    const s = readRawForOrg(id);
    if (s) return s;
  }
  return null;
}

/** @deprecated Use clearImpersonationSession(orgId) instead. */
export function endImpersonationSession(orgId?: string): void {
  if (orgId) {
    clearImpersonationSession(orgId);
    return;
  }
  // End all sessions.
  const toRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k?.startsWith('imp_session_')) toRemove.push(k);
  }
  for (const k of toRemove) sessionStorage.removeItem(k);
  window.dispatchEvent(
    new CustomEvent<ImpersonationSession | null>('impersonation-change', { detail: null }),
  );
}

/** Minutes remaining before the impersonation token for an org expires (0 if none). */
export function impersonationMinutesRemaining(orgId: string): number {
  const s = readRawForOrg(orgId);
  if (!s) return 0;
  return Math.max(0, Math.floor((s.expSeconds - Date.now() / 1000) / 60));
}
